import { Linking, Text, View, type ImageSourcePropType } from "react-native";
import { ClipboardList, ExternalLink, Info } from "lucide-react-native";
import {
  CompactActionButton,
  DetailSheet,
  MoonDetailRow,
  SectionTitle,
  CardTitleText,
  CardSubtitleText,
  MetaText,
} from "@louise/moon-ui";
import { useTranslations } from "@src/packages/i18n";
import { useDateFormat } from "@src/packages/utils/useDateFormat";
import {
  claimEffectiveLabel,
  isFinancialClaimCurrent,
  type FinancialClaim,
  type FinancingProvider,
} from "@src/packages/costs/financialContent";
import FactSheetHeader from "../../components/FactSheetHeader";
import { FINANCIAL_CLAIM_ICONS } from "./financialClaimIcons";
import { EMBER } from "../../../onboarding/theme";
import { semanticColors } from "@louise/theme/tokens";

type Props = {
  provider: FinancingProvider | null;
  logo: ImageSourcePropType | null;
  onClose: () => void;
};

function ClaimBlock({ claim }: { claim: FinancialClaim }) {
  const t = useTranslations();
  const { long: formatLongDate } = useDateFormat();
  return (
    <View
      style={{
        padding: 13,
        borderRadius: 14,
        backgroundColor: semanticColors.action.quiet.background,
      }}
    >
      <MoonDetailRow
        icon={FINANCIAL_CLAIM_ICONS[claim.kind]}
        label={claim.label}
        value={<CardTitleText>{claim.value}</CardTitleText>}
      />
      {claim.caveat ? (
        <CardSubtitleText style={{ marginTop: 5 }}>
          {claim.caveat}
        </CardSubtitleText>
      ) : null}
      <MetaText style={{ marginTop: 7 }}>
        {claimEffectiveLabel(claim, t, formatLongDate)}
      </MetaText>
    </View>
  );
}

export default function FinancingFactSheet({ provider, logo, onClose }: Props) {
  const t = useTranslations();
  const { long: formatLongDate } = useDateFormat();

  if (!provider) return null;

  const currentClaims = provider.claims.filter((claim) =>
    isFinancialClaimCurrent(claim),
  );

  return (
    <DetailSheet
      visible
      onClose={onClose}
      closeLabel={t("costs.financing_page.close_fact_sheet")}
      testID="financing-fact-sheet"
    >
      <FactSheetHeader
        title={provider.name}
        subtitle={provider.productLabel}
        imageSource={logo ?? undefined}
        imageSize={92}
      />

      <View
        style={{
          marginTop: 24,
          padding: 14,
          borderRadius: 14,
          backgroundColor: semanticColors.notice.quiet.background,
        }}
      >
        <SectionTitle icon={Info}>
          {t("costs.financing_page.fact_sheet_summary_heading")}
        </SectionTitle>
        <Text
          className="font-dm-sans"
          style={{
            marginTop: 8,
            fontSize: 15,
            lineHeight: 21,
            color: EMBER.colors.text,
          }}
        >
          {provider.summary.value}
        </Text>
        {provider.summary.caveat ? (
          <Text
            className="font-dm-sans"
            style={{
              marginTop: 5,
              fontSize: 12,
              lineHeight: 17,
              color: EMBER.colors.textMid,
            }}
          >
            {provider.summary.caveat}
          </Text>
        ) : null}
      </View>

      <View style={{ marginTop: 24, marginBottom: 10 }}>
        <SectionTitle icon={ClipboardList}>
          {t("costs.financing_page.fact_sheet_details_heading")}
        </SectionTitle>
      </View>

      {currentClaims.length ? (
        <View style={{ gap: 12 }}>
          {currentClaims.map((claim) => (
            <ClaimBlock key={claim.id} claim={claim} />
          ))}
        </View>
      ) : (
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 13,
            lineHeight: 19,
            color: EMBER.colors.textMid,
          }}
        >
          {t("costs.financing_page.fact_sheet_unavailable")}
        </Text>
      )}

      <Text
        className="font-dm-sans"
        style={{
          marginTop: 24,
          fontSize: 11,
          lineHeight: 16,
          color: EMBER.colors.textFaint,
          textAlign: "center",
        }}
      >
        {t("costs.financing_page.fact_sheet_reviewed", {
          params: {
            date: formatLongDate(provider.summary.provenance.lastVerifiedOn),
            jurisdiction: provider.summary.provenance.jurisdiction,
          },
        })}
      </Text>

      <CompactActionButton
        label={t("costs.financing_page.read_provider_terms")}
        onPress={() => Linking.openURL(provider.url)}
        leadingIcon={ExternalLink}
        variant="primary"
        flexGrow={1}
        minWidth={168}
        minHeight={44}
      />
    </DetailSheet>
  );
}
